/**
 * Validation des opérations reçues par l'API.
 *
 * Frontière de confiance : tout ce qui arrive du réseau est inconnu, y compris
 * ce qu'envoie notre propre client (une version périmée, une file corrompue, un
 * appel direct depuis le futur serveur MCP). Une opération qui ne passe pas
 * cette validation est rejetée sans jamais atteindre D1.
 *
 * La validation est volontairement stricte et sans coercition : on n'essaie pas
 * de réparer une opération malformée, on la refuse.
 */

import type { Op } from './ops.ts';
import { MAX_LABEL_LENGTH, MAX_NAME_LENGTH, MAX_QUANTITY_LENGTH } from './ops.ts';

/** Nombre maximal d'opérations acceptées dans un seul lot. */
export const MAX_BATCH_SIZE = 200;

type Json = Record<string, unknown>;

export function parseOp(input: unknown): Op | null {
  if (typeof input !== 'object' || input === null) return null;
  const raw = input as Json;

  if (!isUuid(raw.op_id) || !isUuid(raw.id)) return null;
  if (!isFiniteNumber(raw.client_ts)) return null;

  const base = { op_id: raw.op_id as string, id: raw.id as string, client_ts: raw.client_ts as number };

  switch (raw.kind) {
    case 'item.create':
      if (!isUuid(raw.list_id)) return null;
      if (!isText(raw.label, MAX_LABEL_LENGTH)) return null;
      if (!isOptionalText(raw.quantity, MAX_QUANTITY_LENGTH)) return null;
      if (!isFiniteNumber(raw.position)) return null;
      if (!isUuid(raw.device_id)) return null;
      return {
        ...base,
        kind: 'item.create',
        list_id: raw.list_id as string,
        label: raw.label as string,
        quantity: (raw.quantity as string | null) ?? null,
        position: raw.position as number,
        device_id: raw.device_id as string,
      };

    case 'item.rename':
      if (!isText(raw.label, MAX_LABEL_LENGTH)) return null;
      if (!isOptionalText(raw.quantity, MAX_QUANTITY_LENGTH)) return null;
      return {
        ...base,
        kind: 'item.rename',
        label: raw.label as string,
        quantity: (raw.quantity as string | null) ?? null,
      };

    case 'item.check':
      if (typeof raw.is_checked !== 'boolean') return null;
      if (!isUuid(raw.device_id)) return null;
      return {
        ...base,
        kind: 'item.check',
        is_checked: raw.is_checked,
        device_id: raw.device_id as string,
      };

    case 'item.move':
      if (!isUuid(raw.list_id)) return null;
      if (!isFiniteNumber(raw.position)) return null;
      return {
        ...base,
        kind: 'item.move',
        list_id: raw.list_id as string,
        position: raw.position as number,
      };

    case 'item.delete':
      return { ...base, kind: 'item.delete' };

    case 'list.create':
      if (!isText(raw.name, MAX_NAME_LENGTH)) return null;
      if (!isFiniteNumber(raw.position)) return null;
      if (typeof raw.is_default !== 'boolean') return null;
      return {
        ...base,
        kind: 'list.create',
        name: raw.name as string,
        position: raw.position as number,
        is_default: raw.is_default,
      };

    case 'list.rename':
      if (!isText(raw.name, MAX_NAME_LENGTH)) return null;
      return { ...base, kind: 'list.rename', name: raw.name as string };

    case 'list.move':
      if (!isFiniteNumber(raw.position)) return null;
      return { ...base, kind: 'list.move', position: raw.position as number };

    case 'list.setDefault':
      return { ...base, kind: 'list.setDefault' };

    case 'list.delete':
      return { ...base, kind: 'list.delete' };

    case 'device.register':
      if (!isText(raw.label, MAX_NAME_LENGTH)) return null;
      if (raw.platform !== 'android' && raw.platform !== 'ios' && raw.platform !== 'other') return null;
      return { ...base, kind: 'device.register', label: raw.label as string, platform: raw.platform };

    case 'device.rename':
      if (!isText(raw.label, MAX_NAME_LENGTH)) return null;
      return { ...base, kind: 'device.rename', label: raw.label as string };

    default:
      return null;
  }
}

/** Analyse un lot. Renvoie les opérations valides et l'index de celles rejetées. */
export function parseBatch(input: unknown): { ops: Op[]; rejected: number[] } | null {
  if (!Array.isArray(input)) return null;
  if (input.length > MAX_BATCH_SIZE) return null;

  const ops: Op[] = [];
  const rejected: number[] = [];

  input.forEach((raw, index) => {
    const op = parseOp(raw);
    if (op) ops.push(op);
    else rejected.push(index);
  });

  return { ops, rejected };
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isUuid(value: unknown): value is string {
  return typeof value === 'string' && UUID_RE.test(value);
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function isText(value: unknown, max: number): value is string {
  return typeof value === 'string' && value.trim().length > 0 && value.length <= max;
}

function isOptionalText(value: unknown, max: number): value is string | null {
  if (value === null || value === undefined) return true;
  return typeof value === 'string' && value.length <= max;
}
